// Home page goal blocks (spec 7): Goals (Monthly / Quarterly / YTD) under "Deals needing attention",
// and Calls & Meetings (This week / This month) under "Coming up this week". Bars are actual vs. the
// pro-rated period target; revenue shows received with the weighted outlook as a lighter overlay.

import { useState } from 'react';
import { Link } from 'react-router';
import { money, todayISO } from '../../lib/format';
import { actualFor, useActuals, useGoals } from '../../lib/goals/data';
import { addMonths, METRIC_LABEL, pace, periodBounds, periodTarget, workdayFraction, type Metric, type Pace, type Period } from '../../lib/goals/engine';
import { Card, cx, Spinner } from '../ui';

const PACE: Record<Pace, { label: string; cls: string }> = {
  ahead: { label: 'ahead', cls: 'bg-emerald-50 text-emerald-700' },
  on_pace: { label: 'on pace', cls: 'bg-sky-50 text-sky-700' },
  behind: { label: 'behind', cls: 'bg-rose-50 text-rose-700' },
  ramping: { label: 'ramping', cls: 'bg-slate-100 text-slate-600' },
};

const fmtCount = (n: number) => (n >= 10 ? Math.round(n).toLocaleString() : n.toFixed(n % 1 ? 1 : 0));

export function MetricBar({ label, actual, target, overlay, isMoney, status }: { label: string; actual: number; target: number; overlay?: number; isMoney?: boolean; status: Pace }) {
  const scale = Math.max(target, actual + (overlay ?? 0), 1);
  const w = (n: number) => `${Math.min(100, (n / scale) * 100)}%`;
  const f = (n: number) => (isMoney ? money(n, false) : fmtCount(n));
  const title = `${f(actual)} of ${f(target)} ${label.toLowerCase()}${overlay ? ` · ${money(overlay, false)} weighted outlook` : ''}`;
  return (
    <div title={title}>
      <div className="mb-1 flex items-center justify-between gap-2 text-sm">
        <span className="font-medium text-slate-700">{label}</span>
        <span className="flex items-center gap-2 text-xs text-slate-500">
          <span className="tabular-nums">
            {f(actual)} / {f(target)}
          </span>
          <span className={cx('rounded px-1.5 py-0.5 font-medium', PACE[status].cls)}>{PACE[status].label}</span>
        </span>
      </div>
      <div className="relative h-3 overflow-hidden rounded-full bg-slate-100" role="img" aria-label={title}>
        {overlay ? <div className="absolute inset-y-0 left-0 bg-indigo-200" style={{ width: w(actual + overlay) }} /> : null}
        <div className="absolute inset-y-0 left-0 rounded-full bg-indigo-600" style={{ width: w(actual) }} />
        {target > 0 && <div className="absolute inset-y-0 w-0.5 bg-slate-500" style={{ left: `calc(${w(target)} - 1px)` }} />}
      </div>
    </div>
  );
}

function Toggle<T extends string>({ value, options, onChange }: { value: T; options: { key: T; label: string }[]; onChange: (v: T) => void }) {
  return (
    <div className="flex rounded-md bg-slate-100 p-0.5 text-xs">
      {options.map((o) => (
        <button key={o.key} onClick={() => onChange(o.key)} className={cx('rounded px-2 py-0.5 font-medium', value === o.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500')}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

const SetupPrompt = () => (
  <p className="text-sm text-slate-500">
    No goals yet.{' '}
    <Link to="/goals" className="font-medium text-indigo-600 hover:underline">
      Set up your goals
    </Link>{' '}
    and this shows your pace against them.
  </p>
);

/** Bars for a set of metrics over a period. */
export function GoalBars({ period, metrics }: { period: Period; metrics: Metric[] }) {
  const { data: g } = useGoals();
  const today = todayISO();
  const bounds = periodBounds(period, today, g?.fiscalStartMonth ?? 1);
  // A goal set mid-period is measured from its start: activity before it doesn't count (spec 11).
  const countFrom = g?.goalStart && g.goalStart > bounds.from ? g.goalStart : bounds.from;
  const { data: a, isLoading } = useActuals(countFrom, bounds.to, g?.timezone ?? 'America/New_York', !!g?.profile);
  if (!g?.profile || !g.plan) return <SetupPrompt />;
  if (isLoading || !a) return <Spinner />;
  const p = g.profile;
  const roles = p.answers.roles;
  const weeks = p.answers.workingWeeks;
  const start = g.goalStart;
  const rampEnds = addMonths(start, p.horizon_months);
  const shown = metrics.filter((m) => m === 'revenue' || g.plan!.annual[m] > 0);
  return (
    <div className="space-y-3">
      {shown.map((m) => {
        const target = periodTarget(g.plan!.annual[m], weeks, bounds, start);
        const actual = actualFor(m, a, roles);
        return (
          <MetricBar
            key={m}
            label={METRIC_LABEL[m]}
            actual={actual}
            target={target}
            overlay={m === 'revenue' ? a.weighted_outlook : undefined}
            isMoney={m === 'revenue'}
            status={pace(actual, target, bounds, today, start, {
              todayFraction: workdayFraction(Number(new Date().toLocaleString('en-US', { timeZone: g.timezone, hour: 'numeric', hourCycle: 'h23' }))), lagging: m === 'revenue' || m === 'deals_done', rampEnds })}
          />
        );
      })}
    </div>
  );
}

export function GoalsBlock() {
  const [period, setPeriod] = useState<Period>('month');
  return (
    <Card
      title="Goals"
      actions={
        <Toggle
          value={period}
          onChange={setPeriod}
          options={[
            { key: 'month', label: 'Monthly' },
            { key: 'quarter', label: 'Quarterly' },
            { key: 'ytd', label: 'YTD' },
          ]}
        />
      }
    >
      <GoalBars period={period} metrics={['revenue', 'meetings', 'bovs', 'engagements', 'listings', 'under_contract', 'deals_done']} />
    </Card>
  );
}

export function CallsMeetingsBlock() {
  const [period, setPeriod] = useState<Period>('week');
  return (
    <Card
      title="Calls & meetings"
      actions={
        <Toggle
          value={period}
          onChange={setPeriod}
          options={[
            { key: 'week', label: 'This week' },
            { key: 'month', label: 'This month' },
          ]}
        />
      }
    >
      <GoalBars period={period} metrics={['calls', 'meetings']} />
    </Card>
  );
}
